import { inject, Injectable, OnDestroy } from '@angular/core';
import { filter, firstValueFrom, Subscription, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { FarmService } from './farm.service';
import { StoreModelService } from './storeModel.service';
import { LegalsService } from './legals.service';
import { xFP } from '../../../assets/data/enum/perm.enum';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { GeneralUtilsService } from './common/generalUtils.service';
import { Farm } from '../_models';
import { FeatureFlagService } from './featureFlag.service';

@Injectable()
export class RoutingUtilitiesService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private featureFlagService = inject(FeatureFlagService);

  constructor(
    private farmService: FarmService,
    private storeModelService: StoreModelService,
    private legalsService: LegalsService,
    private _permissionService: NgxPermissionsService,
    private route: ActivatedRoute,
    private router: Router,
    private generalUtilsService: GeneralUtilsService
  ) {}

  setLatestUrl(url: string) {
    localStorage.setItem('latestUrl', url);
  }

  getLatestUrl(): string {
    return localStorage.getItem('latestUrl');
  }

  async navigateUser() {
    try {
      // Wait for farm data to be available before proceeding
      const farm: Farm = await firstValueFrom(
        this.farmService.getFarmMeFromLocalStore().pipe(
          filter((v) => v !== undefined && v !== null),
          take(1)
        )
      );
      // Safety check
      if (!farm) {
        throw new Error('Farm data is not available');
      }

      if (
        this.router.url === '/auth/payment-succeeded-4e87a1aa' ||
        this.router.url === '/auth/payment-canceled-b0e5bb07' ||
        this.router.url.includes('/upgrade-flow')
      ) {
        return;
      }

      const indexRoute = this.storeModelService.indexRoute.getValue();
      const hasToLegal = this.legalsService.hasToSubmitLegalOptions$.getValue();

      if (indexRoute?.length) {
        if (hasToLegal) {
          this.router.navigate(['/auth/welcome/legals']);
        } else if (await this.isReadOnlyOrNotAdmin()) {
          this.goToIndex(this.route.queryParams, indexRoute);
        } else if (
          (farm.welcomeCheck === true || farm.welcomeCheck === undefined) &&
          ThemeConfig.welcomeFarmCheck
        ) {
          this.router.navigate(['/auth/welcome/farm']);
        } else if (this.route.snapshot.url.length) {
          this.router.navigate([this.route.snapshot.url]);
        } else {
          const latestUrl = this.getLatestUrl();
          if (latestUrl) {
            this.setLatestUrl('');
            this.router.navigate([latestUrl]);
          } else if (['/auth', '/auth/redirect'].includes(this.router.url)) {
            this.goToIndex(this.route.queryParams, indexRoute);
          }
        }
      }
    } catch (error) {
      console.error('Error in navigateUser:', error);
    }
  }

  goToIndex = (params, indexRoute) => {
    const status = params['s'];
    if (!status) {
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || indexRoute;
      this.router.navigate([returnUrl]);
      this.generalUtilsService.loadingCompleted.next(true);
    }
  };

  private async isReadOnlyOrNotAdmin(): Promise<boolean> {
    const hasReadOnlyPermission = await this._permissionService.hasPermission(xFP.RF_ONLYREAD);
    const isAdmin = await this._permissionService.hasPermission(xFP.RF_ADMIN);
    return hasReadOnlyPermission || !isAdmin;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
